/**
 * Transient-error retry for the `qz.printers.*` calls fired immediately after a
 * connection comes up.
 *
 * The QZ Tray websocket accepts a connection before the Java backend's print
 * subsystem has finished initializing. The first printer-subsystem calls in
 * that window (`details`, `getStatus`, `startListening`, ...) reject server-side
 * with a bare `NullPointerException` (see `qz-tray.js`, where the server's
 * `{ error }` response is re-thrown as `new Error(returned.error)`). This is
 * most visible when QZ Tray has just been (re)launched: the first-time printer
 * selector is empty and returning installations show printers stuck on
 * "unknown" status, both of which fix themselves on a page refresh once the
 * backend is warm.
 *
 * We retry only that not-ready signature. Genuine business rejections —
 * "Specified printer could not be found.", "No printer listeners started for
 * this client." — are not transient and must surface immediately.
 */

const DEFAULT_RETRIES = 5;
const DEFAULT_DELAY_MS = 500;
const DEFAULT_BACKOFF_FACTOR = 2;
const DEFAULT_MAX_DELAY_MS = 4_000;

const TRANSIENT_PRINTER_ERROR_PATTERN = /nullpointerexception/i;

const messageOf = (err: unknown): string => {
  if (err instanceof Error) return err.message;

  if (typeof err === "string") return err;

  if (err && typeof err === "object" && "message" in err) {
    const value = (err as { message?: unknown }).message;
    return typeof value === "string" ? value : "";
  }
  return "";
};

/**
 * `true` when the rejection is the "backend not ready yet" `NullPointerException`
 * the print subsystem throws right after the socket opens — i.e. worth retrying.
 */
export const isTransientPrinterError = (err: unknown): boolean =>
  TRANSIENT_PRINTER_ERROR_PATTERN.test(messageOf(err));

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export type RetryOnTransientErrorOptions = {
  backoffFactor?: number;
  delayMs?: number;
  maxDelayMs?: number;
  retries?: number;
  shouldRetry?: (err: unknown) => boolean;
};

/**
 * Run `fn`, retrying with capped exponential backoff while `shouldRetry`
 * (default {@link isTransientPrinterError}) holds and attempts remain. The last
 * error is rethrown once retries are exhausted, so callers see the real failure.
 */
export const retryOnTransientError = async <T>(
  fn: () => Promise<T>,
  {
    backoffFactor = DEFAULT_BACKOFF_FACTOR,
    delayMs = DEFAULT_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    retries = DEFAULT_RETRIES,
    shouldRetry = isTransientPrinterError,
  }: RetryOnTransientErrorOptions = {},
): Promise<T> => {
  let attempt = 0;

  for (;;) {
    try {
      // Retries are inherently sequential: each attempt waits on the previous.
      // eslint-disable-next-line no-await-in-loop
      return await fn();
    } catch (err) {
      if (attempt >= retries || !shouldRetry(err)) throw err;

      // eslint-disable-next-line no-await-in-loop
      await wait(Math.min(delayMs * backoffFactor ** attempt, maxDelayMs));
      attempt += 1;
    }
  }
};
